import { createApi } from '@reduxjs/toolkit/query/react';
import { Invitation, WorkspaceList } from './models';
import fetchBaseQueryCSRF from '../helper';
import { WorkspaceFormData } from 'src/sections/workspaces/view/setup/types';

interface QueryArg {
  id?: number;
  name: string;
  url: string;
  editors?: string[];
  logo?: File;
  datasource?: number[];
}


interface WorkspaceGoogleAuth {
  id: number;
  scopes: string[];
  status: string;
}


// Fonction d'aplatissement
function flattenWorkspaceData(formData: any): any {
  return {
    name: formData.general.name,
    url: formData.general.url,
    friendlyDomains: formData.general.friendlyDomains,
    template: formData.general.template,
    averageCart: formData.general.averageCart,
    currency: formData.general.currency,
    custom: formData.general.custom,
    bqTable: formData.general.bqTable,
    shoppingIncluded: formData.general.shoppingIncluded,
    isDataReady: true, // Valeur par défaut

    // Display
    showSavings: formData.display.showSavings,
    showPlaAlert: formData.display.showPlaAlert,
    showTxtAlert: formData.display.showTxtAlert,

    // Data Config
    selected_countries: formData.dataConfig.selected_countries,
    branded_keywords: formData.dataConfig.branded_keywords,
    excluded_keywords: formData.dataConfig.excluded_keywords,
    exclude_for_non_branded: formData.dataConfig.exclude_for_non_branded,

    country_region_mapping: formData.dataConfig.country_region_mapping,

    // NON BRAND
    nonbrand: formData.dataConfig.nonbrand,
    nonbrand_visibility: formData.dataConfig.nonbrand_visibility,

    // BRAND PULSE
    brandpulse: formData.dataConfig.brandpulse,
    brandpulse_visibility: formData.dataConfig.brandpulse_visibility,

    // CONQUEST MODE
    conquestmode: formData.dataConfig.conquestmode,
    conquestmode_visibility: formData.dataConfig.conquestmode_visibility,

    // Google Ads
    gads_branded_search_campaigns: formData.dataConfig.google_ads.branded_search_campaigns,
    gads_competitor_search_campaigns: formData.dataConfig.google_ads.competitor_search_campaigns,

    gads_branded_shopping_campaigns: formData.dataConfig.google_ads.branded_shopping_campaigns,
    gads_branded_pmax_campaigns: formData.dataConfig.google_ads.branded_pmax_campaigns,
    gads_campaign_region_mapping: formData.dataConfig.google_ads.campaign_region_mapping,
    gads_account_region_mapping: formData.dataConfig.google_ads.account_region_mapping,
    gads_conversion_actions: formData.dataConfig.google_ads.conversion_actions,
    gads_exclude_non_branded: formData.dataConfig.google_ads.exclude_non_branded,


    // Google Analytics
    ga4_conversion_events: formData.dataConfig.google_analytics.conversion_events,

    // Google Search Console
    gsc_country_site_url_mapping: formData.dataConfig.google_search_console.country_site_url_mapping,

    // Objets pour les IDs
    gads: formData.dataConfig.google_ads.customer_id.map((id: any) => ({ id })),
    ga4: formData.dataConfig.google_analytics.property_id.map((propertyId: any) => ({
      ga4_property_id: propertyId,
      ga4_property_name: propertyId // Vous pouvez ajuster selon vos besoins
    })),
    gsc: formData.dataConfig.google_search_console.site_url,

    // Champs de conversions (vides par défaut, à ajuster selon vos besoins)
    conversions_google_ads: [],
    conversions_google_analytics: [],

    // Savings
    savings: formData.savings || [],

    // Champs requis par le backend
    users: [],
    invitations: []
  };
}

const workspaces = createApi({
  baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL + "/workspaces" }),
  reducerPath: 'api.workspaces',
  tagTypes: ['workspaces', 'workspace', 'workspace.google-auth', 'workspace.savings-config'],
  endpoints: (builder) => ({
    createWorkspace: builder.mutation<{ id: number }, QueryArg>({
      query: (body) => ({
        url: `/`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      invalidatesTags: ['workspaces'],
    }),
    updateWorkspace: builder.mutation<{ id: number }, any>({
      query: (formData) => {
        const flattenedData = flattenWorkspaceData(formData);

        return {
          url: `/${formData.general.id || formData.id}`,
          method: 'POST',
          body: flattenedData,
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'max-age=0',
          },
        };
      },
      invalidatesTags: ['workspaces'],
    }),
    deleteWorkspace: builder.mutation<{ id: number }, QueryArg>({
      query: (body) => ({
        url: `/${body.id}`,
        method: 'DELETE',
        body,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      // Sibling of `query`, not a key of what it returns: RTK Query reads the tag
      // off the endpoint definition and ignores anything inside the request
      // descriptor. Without it WorkspaceGuard keeps mirroring the deleted
      // workspace into `currentWorkspace` until a full page reload.
      invalidatesTags: ['workspaces'],
    }),
    getWorkspaces: builder.query<WorkspaceList[], null>({
      query: () => ({
        url: `/`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      // WorkspaceGuard mirrors this list into the Redux `currentWorkspace`, which
      // gates the feature tabs (Local, Non Brand...). Without the tag the list is
      // only refetched on a full page reload.
      providesTags: ['workspaces'],
    }),
    getWorkspaceSetup: builder.query<WorkspaceList, { id: number }>({
      query: ({ id }) => ({
        url: `/${id}/setup`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    getWorkspace: builder.query<WorkspaceList, { id: number }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      providesTags: ["workspace"],
    }),
    getWorkspaceGoogleAuth: builder.query<WorkspaceGoogleAuth[], { id: number }>({
      query: ({ id }) => ({
        url: `/${id}/google-auth`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      providesTags: ["workspace.google-auth"],
    }),
    workspaceUserUpdate: builder.mutation<WorkspaceList, { id: number, userId: number, role: string }>({
      query: ({ id, userId, role }) => ({
        url: `/${id}/users/${userId}`,
        method: 'POST',
        body: { role },
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    workspaceUserDelete: builder.mutation<WorkspaceList, { id: number, userId: number }>({
      query: ({ id, userId }) => ({
        url: `/${id}/users/${userId}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    // Get current user role in workspace
    role: builder.query<{ role: string }, { id: number }>({
      query: ({ id }) => ({
        url: `/${id}/users/me`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    invitations: builder.query<Invitation[], null>({
      query: () => ({
        url: `/invitations`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    invitation: builder.query<Invitation, { id: number, token: string }>({
      query: ({ id, token }) => ({
        url: `/${id}/invitations/${token}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    invite: builder.mutation<Invitation, { workspaceIds: number[], userId: number, userExist?: boolean }>({
      query: ({ workspaceIds, userId, userExist }) => {
        const body: any = { userId, workspaceIds };

        if (userExist !== undefined) {
          body.userExist = userExist;
        }

        return {
          url: `/invitations`,
          method: 'POST',
          body: body,
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'max-age=0',
          },
        };
      },
    }),
    acceptInvitation: builder.mutation<Invitation, { id: number, token: string }>({
      query: ({ id, token }) => ({
        url: `/${id}/invitations/${token}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    deleteInvitation: builder.mutation<Invitation, { workspaceId: number, invitationId: number }>({
      query: ({ workspaceId, invitationId }) => ({
        url: `/${workspaceId}/invitations/${invitationId}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    declineInvitation: builder.mutation<Invitation, { id: number, token: string }>({
      query: ({ id, token }) => ({
        url: `/${id}/invitations/${token}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    getClientsName: builder.query<String[], null>({
      query: () => ({
        url: `/clients`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    // Ajout de la mutation pour ajouter un client
    addClient: builder.mutation<{ message: string, client_id: number }, { name: string, domain_client: string }>({
      query: (body) => ({
        url: `/clients`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
      invalidatesTags: ['workspaces'],  // Invalide le cache après l'ajout
    }),
    getUserWorkspace: builder.query<WorkspaceList[], { workspace_id: number }>({
      query: ({ workspace_id }) => ({
        url: `/${workspace_id}/users`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'max-age=0',
        },
      }),
    }),
    putSetupWorkspace: builder.mutation<any, WorkspaceFormData & { id: number }>({
      query: ({ id, ...body }) => ({
        url: `/${id}/setup`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ['workspaces', { type: 'workspace.savings-config' as const, id }],
    }),
    getSavingsConfig: builder.query<{
      platforms: string[];
      savings_simulation: { platform: string; region_name: string; total_savings_l30d: number | null }[];
      available_regions?: { platform: string; region_name: string }[];
    }, { id: number }>({
      query: ({ id }) => ({
        url: `/${id}/setup/savings-config`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { id }) => [{ type: 'workspace.savings-config', id }],
    }),
    simulateSavings: builder.mutation<{
      platforms: string[];
      savings_simulation: { platform: string; region_name: string; total_savings_l30d: number | null }[];
    }, { id: number; savings: { platform: string; region_name: string; blended_cpc_ref?: number }[] }>({
      query: ({ id, savings }) => ({
        url: `/${id}/setup/savings-config`,
        method: 'POST',
        body: { savings },
      }),
    }),
  }),
});

export const {
  useCreateWorkspaceMutation,
  useGetWorkspacesQuery,
  useGetClientsNameQuery,
  useUpdateWorkspaceMutation,
  useInvitationQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useGetWorkspaceQuery,
  useWorkspaceUserDeleteMutation,
  useDeleteInvitationMutation,
  useWorkspaceUserUpdateMutation,
  useInviteMutation,
  useInvitationsQuery,
  useRoleQuery,
  useLazyRoleQuery,
  useDeleteWorkspaceMutation,
  useAddClientMutation,
  useGetUserWorkspaceQuery,
  useGetWorkspaceGoogleAuthQuery,
  usePutSetupWorkspaceMutation,
  useLazyGetWorkspaceQuery,
  useGetWorkspaceSetupQuery,
  useGetSavingsConfigQuery,
  useSimulateSavingsMutation,
} = workspaces;
export default workspaces;
